import { Ticker } from "pixi.js";
import { ILogger } from "../logger/ILogger";
import { VariableSaver } from "../../tools/variableSaver/VariableSaver";

type PerformaceInfo = {
    timestamp: number;
    sessionDuration: number;
    averageFPS: number;
}

export class PerformanceTracker {
    private static readonly SAVE_INTERVAL = 60000;
    private static readonly PERFORMANCE_INFO_KEY = "performance_info";

    private readonly ticker: Ticker;

    private sessionDuration: number /*min*/;
    private averageFPS: number;

    private fps: number;
    private framesCount: number;
    private framesTime: number;

    private saveTime: number;

    public constructor(
        private readonly variableSaver: VariableSaver,
        private readonly logger: ILogger
    ) {
        this.ticker = Ticker.shared;
        this.sessionDuration = 0;
        this.averageFPS = -1;
        this.fps = 0;
        this.framesCount = 0;
        this.framesTime = 0;
        this.saveTime = 0;

        this.checkForStoredEvent();
        this.beforeUnload = this.beforeUnload.bind(this);

        this.ticker.add(this.onTick, this);
        window.addEventListener("beforeunload", this.beforeUnload);
    }

    private checkForStoredEvent(): void {
        const value = this.variableSaver.getString(PerformanceTracker.PERFORMANCE_INFO_KEY);
        if (value == null || value.length == 0) {
            this.logger.debug("[PerformanceTracker] No stored event");
            return;
        }
        try {
            const info = JSON.parse(value) as PerformaceInfo;
            this.sendEvent(info, true);
        }
        catch (e: unknown) {
            this.logger.debug(`Failed to parse stored performance event, reason: ${e}`);
            this.variableSaver.saveString(PerformanceTracker.PERFORMANCE_INFO_KEY, "");
        }
    }

    private onTick(): void {
        this.framesCount++;
        this.framesTime += this.ticker.elapsedMS;
        this.sessionDuration += this.ticker.elapsedMS / 60000;
        this.saveTime += this.ticker.elapsedMS;

        if (this.saveTime >= PerformanceTracker.SAVE_INTERVAL) {
            this.saveSessionInfo();
            this.saveTime = 0;
        }

        if (this.framesTime < 1000) {
            return;
        }

        this.fps = this.framesCount;
        this.framesCount = 0;
        this.framesTime = 0;

        if (this.averageFPS == -1) {
            this.averageFPS = this.fps;
            return;
        }

        this.averageFPS = Math.floor((this.averageFPS + this.fps) * 0.5);
    }

    private beforeUnload(): void {
        if (this.sessionDuration > 0.1) {
            this.sendEvent({ averageFPS: this.averageFPS, sessionDuration: this.sessionDuration, timestamp: Date.now() });
        }
        this.variableSaver.saveString(PerformanceTracker.PERFORMANCE_INFO_KEY, "");
    }

    private saveSessionInfo(): void {
        const info: PerformaceInfo = {
            averageFPS: this.averageFPS,
            sessionDuration: this.sessionDuration,
            timestamp: Date.now()
        };
        this.variableSaver.saveString(PerformanceTracker.PERFORMANCE_INFO_KEY, JSON.stringify(info));
    }

    private sendEvent(info: PerformaceInfo, fromInitialization?: boolean): void {
        this.logger.event("Performance Event", {
            sessionDuration: info.sessionDuration.toFixed(2),
            averageFPS: info.averageFPS.toString(),
            raisedFrom: fromInitialization ? "game init" : "game close",
            timestamp: info.timestamp.toString()
        });
    }
}